import { useEffect, useState } from "react";
import api from "@/lib/api";
import { LargeTitle, Surface, Pill, Skeleton, EmptyState } from "@/components/ios";
import { Scale, Info, Clock, CalendarClock, Repeat, MailCheck, Flame, FileClock } from "lucide-react";

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2.5 py-2 border-b last:border-0" style={{ borderColor: "var(--border)" }}>
    <Icon size={15} className="mt-0.5 accent-text shrink-0" />
    <div className="min-w-0">
      <div className="text-[11px] uppercase tracking-wide text-secondary">{label}</div>
      <div className="text-sm" style={{ color: "var(--text)" }}>{value}</div>
    </div>
  </div>
);

export default function TenancyRules() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/tenancy-rules").then(({ data }) => setData(data)); }, []);

  const rules = data?.rules || [];
  const operating = rules.filter((r) => r.operating);
  const others = rules.filter((r) => !r.operating);
  const ordered = [...operating, ...others];

  return (
    <div>
      <LargeTitle subtitle="Per-state residential tenancy rules applied across inspections, notices and reports">Tenancy Rules</LargeTitle>

      <Surface className="p-3.5 mb-6 flex items-start gap-2.5" data-testid="rules-disclaimer" style={{ background: "var(--surface-2)" }}>
        <Info size={16} className="mt-0.5 shrink-0" style={{ color: "var(--warning)" }} />
        <p className="text-xs text-secondary">{data?.disclaimer || "Guidance only — not legal advice. Verify figures against current legislation."}</p>
      </Surface>

      {!data ? (
        <div className="grid md:grid-cols-2 gap-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-64" />)}</div>
      ) : rules.length === 0 ? (
        <Surface><EmptyState icon={Scale} title="No rules available" /></Surface>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {ordered.map((r) => (
            <Surface key={r.state} className="p-5" data-testid={`rules-${r.state}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2.5">
                  <div className="h-10 w-10 rounded-xl grid place-items-center font-bold text-sm" style={{ background: "var(--accent-soft)", color: "var(--accent)" }}>{r.state}</div>
                  <div className="font-semibold" style={{ color: "var(--text)" }}>{r.state}</div>
                </div>
                {r.operating && <Pill tone="accent">Operating</Pill>}
              </div>
              <p className="text-xs text-secondary mb-3">{r.act}</p>
              <Row icon={Clock} label="Minimum entry notice" value={`${r.min_notice_days} days`} />
              <Row icon={CalendarClock} label="Permitted entry hours" value={r.entry_hours} />
              <Row icon={Repeat} label="Routine inspection cap" value={`${r.routine_max_per_year} per year`} />
              <Row icon={MailCheck} label="Tenant response window" value={`${r.tenant_response_window_days} days`} />
              <Row icon={FileClock} label="Condition report provision" value={r.entry_report_note} />
              <Row icon={Flame} label="Smoke alarms" value={r.smoke_alarm} />
            </Surface>
          ))}
        </div>
      )}
    </div>
  );
}
